import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import {
  User,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  ConfirmationResult,
  createUserWithEmailAndPassword,
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { auth } from '../config/firebase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  sendPhoneVerification: (phoneNumber: string) => Promise<ConfirmationResult>;
  verifyPhoneCode: (
    confirmationResult: ConfirmationResult,
    code: string
  ) => Promise<void>;
  recaptchaVerifier: RecaptchaVerifier | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [recaptchaVerifier, setRecaptchaVerifier] =
    useState<RecaptchaVerifier | null>(null);

  // Check if user is admin
  const checkAdminStatus = async (user: User | null) => {
    if (!user) {
      setIsAdmin(false);
      return;
    }

    try {
      const tokenResult = await user?.getIdTokenResult(true);
      console.log('Token claims:', tokenResult?.claims);
      if (tokenResult?.claims) {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      await checkAdminStatus(user);
      setLoading(false);
    });

    return () => {
      unsubscribe();
      // Clean up reCAPTCHA verifier if it exists and is not destroyed
      if (recaptchaVerifier) {
        try {
          recaptchaVerifier.clear();
        } catch (error) {
          // Ignore cleanup errors - verifier might already be destroyed
          console.log('reCAPTCHA verifier already cleaned up');
        }
        setRecaptchaVerifier(null);
      }

      // Clear the DOM container as well
      const container = document.getElementById('recaptcha-container');
      if (container) {
        container.innerHTML = '';
      }
    };
  }, [recaptchaVerifier]);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      setUser(userCredential.user);
      await checkAdminStatus(userCredential.user);
    } catch (error) {
      throw error;
    }
  };

  const register = async (email: string, password: string): Promise<void> => {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );

      // Create user document in Firestore
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        email: userCredential.user.email,
        isAdmin: false, // Default to non-admin
        createdAt: new Date(),
      });

      setUser(userCredential.user);
      await checkAdminStatus(userCredential.user);
    } catch (error) {
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await signOut(auth);
      setUser(null);
      setIsAdmin(false);
    } catch (error) {
      throw error;
    }
  };

  const sendPhoneVerification = async (
    phoneNumber: string
  ): Promise<ConfirmationResult> => {
    try {
      // Format phone number to include country code if not present
      let formattedPhoneNumber = phoneNumber;
      if (!phoneNumber.startsWith('+')) {
        // Default to US country code if not specified
        formattedPhoneNumber = '+1' + phoneNumber.replace(/\D/g, '');
      }

      console.log(
        '📱 Attempting phone verification for:',
        formattedPhoneNumber
      );
      console.log('🔧 Firebase app:', auth.app.name);
      console.log('🌐 Current domain:', window.location.hostname);

      // Create reCAPTCHA verifier if it doesn't exist
      let verifier = recaptchaVerifier;
      if (!verifier) {
        console.log('🔄 Creating new reCAPTCHA verifier...');

        // Clear any existing reCAPTCHA in the container
        const container = document.getElementById('recaptcha-container');
        if (container) {
          container.innerHTML = '';
        }

        verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
          size: 'invisible',
          callback: () => {
            console.log('✅ reCAPTCHA solved');
          },
          'expired-callback': () => {
            console.log('⏰ reCAPTCHA expired');
            setRecaptchaVerifier(null);
          },
          'error-callback': () => {
            console.log('❌ reCAPTCHA error');
            setRecaptchaVerifier(null);
          },
        });
        setRecaptchaVerifier(verifier);
      }

      console.log('📤 Sending verification code...');
      const confirmationResult = await signInWithPhoneNumber(
        auth,
        formattedPhoneNumber,
        verifier
      );
      console.log('✅ Verification code sent successfully');
      return confirmationResult;
    } catch (error: any) {
      console.error('❌ Phone verification error:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);

      // Clean up verifier on error
      if (recaptchaVerifier) {
        try {
          recaptchaVerifier.clear();
        } catch (cleanupError) {
          // Ignore cleanup errors - verifier might already be destroyed
          console.log('reCAPTCHA verifier already cleaned up');
        }
        setRecaptchaVerifier(null);
      }

      // Clear the DOM container as well
      const container = document.getElementById('recaptcha-container');
      if (container) {
        container.innerHTML = '';
      }

      // Provide more specific error messages
      if (error.code === 'auth/invalid-phone-number') {
        throw new Error(
          'Invalid phone number format. Please include country code (e.g., +1234567890)'
        );
      } else if (error.code === 'auth/too-many-requests') {
        throw new Error(
          'Too many SMS requests. Please wait 1-2 hours before trying again, or use a different phone number. For testing, try +1 650-555-3434.'
        );
      } else if (error.code === 'auth/invalid-app-credential') {
        throw new Error(
          'Phone authentication is not properly configured in Firebase Console. Please enable phone authentication and add your domain to authorized domains.'
        );
      } else if (error.code === 'auth/operation-not-allowed') {
        throw new Error(
          'Phone authentication is not enabled. Please enable it in Firebase Console.'
        );
      } else {
        throw new Error(error.message || 'Failed to send verification code');
      }
    }
  };

  const verifyPhoneCode = async (
    confirmationResult: ConfirmationResult,
    code: string
  ): Promise<void> => {
    try {
      const result = await confirmationResult.confirm(code);
      setUser(result.user);

      // Clean up reCAPTCHA verifier after successful verification
      if (recaptchaVerifier) {
        try {
          recaptchaVerifier.clear();
        } catch (cleanupError) {
          // Ignore cleanup errors - verifier might already be destroyed
          console.log('reCAPTCHA verifier already cleaned up');
        }
        setRecaptchaVerifier(null);
      }

      // Clear the DOM container as well
      const container = document.getElementById('recaptcha-container');
      if (container) {
        container.innerHTML = '';
      }
    } catch (error: any) {
      console.error('Phone code verification error:', error);

      // Provide more specific error messages
      if (error.code === 'auth/invalid-verification-code') {
        throw new Error(
          'Invalid verification code. Please check and try again.'
        );
      } else if (error.code === 'auth/code-expired') {
        throw new Error(
          'Verification code has expired. Please request a new one.'
        );
      } else if (error.code === 'auth/invalid-verification-id') {
        throw new Error('Invalid verification session. Please start over.');
      } else {
        throw new Error(error.message || 'Failed to verify code');
      }
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    isAdmin,
    login,
    register,
    logout,
    sendPhoneVerification,
    verifyPhoneCode,
    recaptchaVerifier,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
